import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Loader2, FileText, MessageSquareText, Save, PenLine, History } from "lucide-react";
import { toast } from "sonner";
import { axiosInstance } from "../../App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AgeRangePicker from "./AgeRangePicker";
import CommentSuggestions from "./CommentSuggestions";
import CommentHistory from "./CommentHistory";
import { buildTermOptions, currentTermKey, termLabel, termMonths } from "./termUtils";

const STAFF_ROLES = ["admin", "guru", "branch_manager", "superadmin"];

export const triggerBlobDownload = (data, filename, type) => {
  const url = window.URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export const blobErrorMessage = async (error, fallback) => {
  try {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      const parsed = JSON.parse(await data.text());
      return parsed?.detail || fallback;
    }
    return data?.detail || fallback;
  } catch {
    return fallback;
  }
};

export default function ReportDownloadDialog({ open, onOpenChange, child, user, milestones, ageOrder }) {
  const isStaff = STAFF_ROLES.includes(user?.role);
  const [selectedAges, setSelectedAges] = useState([]);
  const [tab, setTab] = useState("comment");
  const [term, setTerm] = useState(currentTermKey());
  const [comment, setComment] = useState("");
  const [history, setHistory] = useState([]);
  const [currentProgress, setCurrentProgress] = useState(null);
  const [loadingComment, setLoadingComment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleteTerm, setDeleteTerm] = useState(null);

  const ageOptions = useMemo(() => {
    const present = new Set(milestones.map((m) => m.age_range));
    return ageOrder.filter((a) => present.has(a)).concat([...present].filter((a) => !ageOrder.includes(a)));
  }, [milestones, ageOrder]);

  const selectedCount = useMemo(() => {
    if (selectedAges.length === 0) return milestones.length;
    return milestones.filter((m) => selectedAges.includes(m.age_range)).length;
  }, [milestones, selectedAges]);

  const termOptions = useMemo(() => buildTermOptions(history.map((h) => h.term_key)), [history]);
  const savedComment = useMemo(() => history.find((h) => h.term_key === term) || null, [history, term]);

  const loadHistory = useCallback(async () => {
    if (!child?.id) return [];
    const res = await axiosInstance.get(`/development/report-comments/${child.id}/history`);
    setHistory(res.data?.history || []);
    setCurrentProgress(res.data?.current_progress || null);
    return res.data?.history || [];
  }, [child?.id]);

  useEffect(() => {
    if (!open || !child?.id) return;
    setSelectedAges([]);
    setTab("comment");
    const cur = currentTermKey();
    setTerm(cur);
    let active = true;
    setLoadingComment(true);
    loadHistory()
      .then((list) => { if (active) setComment(list.find((h) => h.term_key === cur)?.comment || ""); })
      .catch(() => { if (active) { setHistory([]); setComment(""); } })
      .finally(() => { if (active) setLoadingComment(false); });
    return () => { active = false; };
  }, [open, child?.id, loadHistory]);

  const changeTerm = (key) => {
    setTerm(key);
    setComment(history.find((h) => h.term_key === key)?.comment || "");
  };

  const dirty = isStaff && comment.trim() !== (savedComment?.comment || "");

  const saveComment = async (silent = false) => {
    if (!child?.id) return false;
    setSaving(true);
    try {
      const res = await axiosInstance.put(`/development/report-comment/${child.id}`, { comment, term });
      setComment(res.data?.comment || "");
      await loadHistory();
      if (!silent) toast.success(`Komen ${termLabel(term)} disimpan`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat menyimpan komen");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const key = deleteTerm;
    setDeleteTerm(null);
    try {
      await axiosInstance.delete(`/development/report-comment/${child.id}`, { params: { term: key } });
      const list = await loadHistory();
      if (key === term) setComment(list.find((h) => h.term_key === term)?.comment || "");
      toast.success(`Komen ${termLabel(key)} dipadam`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat memadam komen");
    }
  };

  const insertSentence = (sentence) => {
    setComment((prev) => {
      const base = prev.trimEnd();
      return (base ? `${base} ${sentence}` : sentence).slice(0, 3000);
    });
  };

  const useFull = (text) => {
    setComment(text.slice(0, 3000));
    toast.success("Komen lengkap dimasukkan - sila semak & ubah suai");
  };

  const download = async () => {
    if (!child?.id) return;
    if (dirty) {
      const ok = await saveComment(true);
      if (!ok) return;
    }
    setDownloading(true);
    try {
      const params = { term };
      if (selectedAges.length) params.age_ranges = selectedAges.join(",");
      const res = await axiosInstance.get(`/development/report/${child.id}`, { params, responseType: "blob" });
      const suffix = selectedAges.length ? `_${selectedAges.length}_peringkat` : "";
      triggerBlobDownload(res.data, `Laporan_Perkembangan_${(child.name || "anak").replace(/\s+/g, "_")}_${term}${suffix}.pdf`, "application/pdf");
      toast.success("Laporan PDF dimuat turun!");
      onOpenChange(false);
    } catch (error) {
      toast.error(await blobErrorMessage(error, "Ralat memuat turun laporan"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-dialog" className="sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-purple-800">
            <FileText className="w-5 h-5" /> Muat Turun Laporan Perkembangan
          </DialogTitle>
          <DialogDescription className="text-left">
            {child?.name}{child?.class_name ? ` - ${child.class_name}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-800">Peringkat Umur</h4>
              <span data-testid="report-selected-count" className="text-xs text-gray-500">{selectedCount} item perkembangan</span>
            </div>
            <AgeRangePicker options={ageOptions} value={selectedAges} onChange={setSelectedAges} testIdPrefix="report-age" />
          </section>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="comment" data-testid="tab-comment"><MessageSquareText className="w-4 h-4 mr-1.5" /> Komen Penggal</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-1.5" /> Sejarah ({history.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="comment" className="mt-3">
              <section className="rounded-xl border border-purple-100 bg-purple-50/50 p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-gray-800">Komen Guru</h4>
                  <Select value={term} onValueChange={changeTerm}>
                    <SelectTrigger data-testid="term-select" className="w-[220px] bg-white h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {termOptions.map((k) => (
                        <SelectItem key={k} value={k}>
                          {termLabel(k)} ({termMonths(k)}){history.some((h) => h.term_key === k) ? " \u2022" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {loadingComment ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Memuatkan komen...</div>
                ) : isStaff ? (
                  <>
                    <Textarea
                      data-testid="report-comment-input"
                      value={comment}
                      onChange={(e) => setComment(e.target.value.slice(0, 3000))}
                      rows={5}
                      placeholder="Tulis komen tentang perkembangan, kekuatan dan cadangan untuk anak ini..."
                      className="bg-white resize-y"
                    />
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs text-gray-500">
                        {savedComment?.updated_at
                          ? `Disimpan ${new Date(savedComment.updated_at).toLocaleDateString("ms-MY")}${savedComment.teacher_name ? ` oleh ${savedComment.teacher_name}` : ""}`
                          : "Belum disimpan bagi penggal ini"}
                        {" "}&middot; {comment.length}/3000
                      </span>
                      <Button
                        type="button" size="sm" variant="outline" data-testid="report-comment-save"
                        disabled={!dirty || saving} onClick={() => saveComment(false)}
                        className="border-purple-200 text-purple-700 hover:bg-purple-50"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                        Simpan Komen
                      </Button>
                    </div>
                    <CommentSuggestions childId={child?.id} ageRanges={selectedAges} onInsert={insertSentence} onUseFull={useFull} />
                  </>
                ) : (
                  <p data-testid="report-comment-readonly" className="text-sm text-gray-700 whitespace-pre-line">
                    {savedComment?.comment || "Tiada komen guru bagi penggal ini."}
                  </p>
                )}
              </section>
            </TabsContent>

            <TabsContent value="history" className="mt-3">
              <CommentHistory
                history={history}
                currentProgress={currentProgress}
                isStaff={isStaff}
                onEdit={(key) => { changeTerm(key); setTab("comment"); }}
                onDelete={(key) => setDeleteTerm(key)}
              />
            </TabsContent>
          </Tabs>

          <div className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 rounded-lg p-3">
            <PenLine className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
            <span>PDF memaparkan komen {termLabel(term)}, perbandingan kemajuan penggal terdahulu, dan ruang tandatangan Guru Kelas, Penyelia / Pengurus serta Ibu Bapa / Penjaga.</span>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={downloading}>Batal</Button>
          <Button
            data-testid="report-download-confirm"
            onClick={download}
            disabled={downloading || saving || selectedCount === 0}
            className="bg-purple-700 hover:bg-purple-800 text-white font-semibold"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            {downloading ? "Menjana PDF..." : "Muat Turun PDF"}
          </Button>
        </DialogFooter>

        <AlertDialog open={!!deleteTerm} onOpenChange={(v) => { if (!v) setDeleteTerm(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Padam komen {termLabel(deleteTerm)}?</AlertDialogTitle>
              <AlertDialogDescription>Komen dan rekod kemajuan penggal ini akan dipadam dari sejarah.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Batal</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-delete-comment" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Padam</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
